/** The device families a passkey label is prefilled with. */
export type PasskeyDeviceKind = 'Mobile' | 'Mac' | 'Windows' | 'Linux' | 'Desktop';

/**
 * Best-effort device family from the browser. Mobile wins over the OS (an
 * iPhone is "Mobile", not "Mac"); an unrecognised desktop UA is "Desktop".
 */
export function detectPasskeyDeviceKind(
    userAgent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
    hints: { mobile?: boolean; platform?: string } | null = typeof navigator === 'undefined'
        ? null
        : ((navigator as { userAgentData?: { mobile?: boolean; platform?: string } }).userAgentData ?? null),
): PasskeyDeviceKind {
    const ua = userAgent.toLowerCase();
    const platform = (hints?.platform ?? '').toLowerCase();
    if (
        hints?.mobile === true ||
        /android|iphone|ipad|ipod|mobile|windows phone/.test(ua) ||
        // iPadOS reports itself as a Mac with touch support
        (/macintosh/.test(ua) && typeof navigator !== 'undefined' && (navigator.maxTouchPoints ?? 0) > 1)
    ) {
        return 'Mobile';
    }
    if (platform.includes('mac') || /macintosh|mac os x/.test(ua)) return 'Mac';
    if (platform.includes('windows') || /windows/.test(ua)) return 'Windows';
    if (platform.includes('linux') || platform.includes('chrome os') || /linux|cros|x11/.test(ua)) return 'Linux';
    return 'Desktop';
}

/**
 * "Mac 1", "Mac 2", …: the lowest number not already used by a label of the
 * same family among the registered passkeys, so a removed key's number is
 * reused and nothing ever collides with what the user already has.
 */
export function suggestPasskeyLabel(
    kind: PasskeyDeviceKind,
    existingLabels: readonly (string | null | undefined)[],
): string {
    const taken = new Set<number>();
    const pattern = new RegExp(`^${kind}\\s+(\\d+)$`, 'i');
    for (const label of existingLabels) {
        const match = label?.trim().match(pattern);
        if (match) taken.add(Number(match[1]));
    }
    let n = 1;
    while (taken.has(n)) n += 1;
    return `${kind} ${n}`;
}
